import { useEffect, useSyncExternalStore } from "react";
import { Link } from "wouter";
import { Activity, X } from "lucide-react";
import { ASSISTANTS, ASSISTANT_IDS, activityMeta, activityText, toastMessage, type ActivityEvent } from "@workspace/assistants";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { showPillToast } from "@/components/calendar/pill-toast";
import { useAuth } from "@/hooks/useAuth";
import { useAssistantActivity, useAssistantsOverview, useRefreshAfterAssistantChanges } from "@/hooks/use-assistants";
import { AssistantMark, INK, MUTED, PURPLE } from "@/components/assistant/assistant-ui";
import { cn } from "@/lib/utils";

// ── The drawer's open state, shared by the toast action and anything else that opens it ──

let drawerOpen = false;
const listeners = new Set<() => void>();
function setDrawer(open: boolean) {
  drawerOpen = open;
  listeners.forEach((l) => l());
}
export const openActivityDrawer = () => setDrawer(true);
function useDrawerOpen() {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => drawerOpen,
  );
}

const SEEN_KEY = "unfold_assistant_activity_seen";
const POLL_MS = 30_000;

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

function writeSeen(at: string) {
  try {
    localStorage.setItem(SEEN_KEY, at);
  } catch {
    // Private mode: toasts may repeat after a reload, nothing worse.
  }
}

/**
 * Mounted once for signed-in people. While an assistant is connected it checks for new
 * activity every 30 seconds and whenever UnfoldWIN comes back into focus; anything new
 * refreshes the screens that show steps (the calendar's too, when a step was scheduled)
 * and gets one notice ("Claude added 3 steps to Google Calendar · View activity",
 * AI 6/9/19/22). The notice is the calendar layer's pill toast, so it and the calendar's
 * own confirmations share one spot and never stack on top of each other.
 */
export function AssistantActivityHost() {
  const { isAuthenticated } = useAuth();
  const overview = useAssistantsOverview(isAuthenticated);
  const watching = isAuthenticated && (overview.data?.connections.some((c) => c.status !== "not_connected") ?? false);
  const activity = useAssistantActivity({ enabled: watching, refetchInterval: watching ? POLL_MS : false });
  const refresh = useRefreshAfterAssistantChanges();

  useEffect(() => {
    if (!isAuthenticated) return;
    const onBack = () => {
      if (document.visibilityState !== "visible") return;
      void overview.refetch();
      if (watching) void activity.refetch();
    };
    window.addEventListener("focus", onBack);
    document.addEventListener("visibilitychange", onBack);
    return () => {
      window.removeEventListener("focus", onBack);
      document.removeEventListener("visibilitychange", onBack);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, watching]);

  useEffect(() => {
    const events = activity.data;
    if (!events) return;
    const newest = events[0]?.at ?? new Date().toISOString();
    // First time on this device: what an assistant did since it connected today is news;
    // older history isn't.
    const connectedToday = events.find((e) => e.kind === "connected" && Date.now() - Date.parse(e.at) < 86_400_000);
    const seen = readSeen() ?? connectedToday?.at ?? newest;
    const fresh = events.filter((e) => e.at > seen);
    if (fresh.length === 0) return writeSeen(seen);
    writeSeen(fresh[0].at);
    refresh(fresh);
    const messages = ASSISTANT_IDS.flatMap((assistant) => {
      const message = toastMessage(fresh.filter((e) => e.assistant === assistant));
      return message ? [{ assistant, message }] : [];
    });
    if (messages.length === 0) return;
    showPillToast({
      // One pill at a time: when both assistants made changes, one line names them both.
      message:
        messages.length === 1
          ? messages[0].message
          : `${messages.map((m) => ASSISTANTS[m.assistant].name).join(" and ")} made changes`,
      action: { label: "View activity", onClick: openActivityDrawer },
      durationMs: 8000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.data]);

  return isAuthenticated ? <ActivityDrawer events={activity.data ?? []} /> : null;
}

/** AI 6 / AI 19: the floating "Assistant activity" panel over Momentum. */
function ActivityDrawer({ events }: { events: ActivityEvent[] }) {
  const open = useDrawerOpen();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDrawer(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  return (
    <aside
      role="dialog"
      aria-label="Assistant activity"
      className={cn(
        "fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+84px)] z-[62] flex max-h-[min(70dvh,560px)] flex-col gap-3 rounded-[22px] border bg-white p-[22px] shadow-[0_16px_20px_rgba(28,25,23,0.16)]",
        "md:inset-x-auto md:bottom-6 md:right-6 md:w-[360px]",
        "border-[rgba(47,41,37,0.08)]",
      )}
    >
      <div className="flex items-center gap-2">
        <Activity className={`size-4 ${PURPLE}`} />
        <h2 className={`flex-1 text-base font-semibold ${INK}`}>Assistant activity</h2>
        <button
          type="button"
          onClick={() => setDrawer(false)}
          aria-label="Close assistant activity"
          className={`rounded-md p-0.5 ${MUTED} transition-colors hover:text-[#2f2925] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e53ac]/40`}
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="-mx-1 min-h-0 overflow-y-auto px-1">
        <ActivityList events={events.slice(0, 8)} />
      </div>
      <Link href="/settings#ai-assistants" onClick={() => setDrawer(false)} className={`w-fit text-xs font-medium ${PURPLE} hover:underline`}>
        Manage in Settings → AI assistants
      </Link>
    </aside>
  );
}

/** Settings → AI assistants → Assistant activity: everything, newest first. */
export function AssistantActivityDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { data: events, isLoading } = useAssistantActivity({ enabled: open });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-[rgba(47,41,37,0.28)]"
        className="max-h-[88dvh] max-w-[480px] gap-4 overflow-y-auto rounded-[28px] border-0 bg-white p-6 sm:rounded-[28px] sm:p-7"
      >
        <div className="flex items-center gap-2">
          <Activity className={`size-4 ${PURPLE}`} />
          <DialogTitle className={`text-lg font-semibold ${INK}`}>Assistant activity</DialogTitle>
        </div>
        <DialogDescription className={`-mt-2 text-[13px] ${MUTED}`}>
          Everything Claude and ChatGPT did in UnfoldWIN, and when. Disconnecting removes access at once.
        </DialogDescription>
        {isLoading ? (
          <p className={`text-sm ${MUTED}`}>Loading…</p>
        ) : events && events.length > 0 ? (
          <ActivityList events={events} />
        ) : (
          <p className={`rounded-2xl bg-[#f8f6f2] p-4 text-sm ${MUTED}`}>
            Nothing yet. When an assistant ticks a step, adds a note or plans your week, it shows up here.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ActivityList({ events }: { events: ActivityEvent[] }) {
  const now = new Date();
  return (
    <ul className="flex flex-col">
      {events.map((e, i) => (
        <li key={e.id} className={cn("flex gap-[10px] py-3", i > 0 && "border-t border-[rgba(47,41,37,0.08)]", i === 0 && "pt-0")}>
          <AssistantMark assistant={e.assistant} size={26} />
          <div className="min-w-0 flex-1">
            <p className={`text-[13px] font-medium leading-[19px] ${INK}`}>{activityText(e, now)}</p>
            <p className={`mt-0.5 text-xs ${MUTED}`}>{activityMeta(e, now)}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}
